// CSV rows for the four SRA views.
//
// Each export covers what its view is SHOWING, which differs by pane:
//
//   Sessions  — every matching session: the search filter applies, paging does
//               not. The toolbar says "100 matching", so 100 rows come out.
//   Matrix    — the grid as displayed, which is the current page of takes
//               against the current columns.
//   Results   — the selected sessions only; that pane is already scoped to the
//               selection and shows nothing without one.
//   Timeline  — the selected sessions, every exchange, not only search hits.
//
// Audio and uploads are INDICATED, never exported. A CSV cell says whether a
// recording or a file exists for that answer; the artifacts themselves stay
// where they are. Putting a storage path in a spreadsheet implies a file that
// can be opened from it, which is not true.
//
// UI-FREE: no React. Safe to unit-test in isolation.

import { extractGcsPath, uploadFilename, mapNonAnswer } from '../../utils/exploreData';

const text = (value) => (value === null || value === undefined ? '' : String(value));

// A timeline cell can be handed a string, an array of utterances, or a whole
// structured payload. `String(object)` yields "[object Object]", which is how a
// column of real content turns into a column of nothing, so every value that
// reaches a cell goes through here first.
const plainText = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(plainText).filter(Boolean).join(' ');
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (_) {
      return '';
    }
  }
  return '';
};

const json = (value, indent = 0) => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, indent);
  } catch (_) {
    return '';
  }
};

const bytesLabel = (n) => {
  if (!Number.isFinite(n)) return '';
  if (n < 1024) return `${n} bytes`;
  const kb = n / 1024;
  return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`;
};
const when = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
};

/** Present / absent, for an artifact the CSV names but does not carry. */
export const artifactFlag = (value) => (value ? 'Yes' : '');

export const SESSION_COLUMNS = [
  { header: 'Recipient', get: (r) => r.recipient },
  { header: 'Session', get: (r) => r.sessionId },
  { header: 'Started', get: (r) => r.started },
  { header: 'Status', get: (r) => r.status },
];

/** Every matching session — the search filter, all pages. */
export function buildSessionRows(sessions = [], recipientHandle = () => '') {
  return sessions.map(s => ({
    recipient: text(recipientHandle(s)),
    sessionId: text(s.session_id),
    started: when(s.started_at || s.ts_start || s.ts || s.created_at),
    // Same fallback the Sessions table shows: a take with no recorded status
    // is completed if it ended, otherwise still active. Blank would read as
    // missing data when the screen beside it says "active".
    status: text(s.completion_status || s.status || (s.completed_at || s.ts_end ? 'completed' : 'active')),
  }));
}

// The grid keeps its cells in ONE flat map keyed "<session> <column>", which is
// how MatrixView reads them. A nested cells[session][column] lookup finds
// nothing there, so every answer column exports blank. Flat first, nested kept
// for any caller that still passes a two-level map.
const matrixCell = (cells, rowKey, columnName) => {
  const flat = cells?.[`${rowKey} ${columnName}`];
  if (flat !== undefined) return flat;
  return cells?.[rowKey]?.[columnName];
};

/**
 * The matrix as displayed: one row per take, one column per question, plus the
 * system columns the grid carries. Every column is written, including any the
 * column picker is currently hiding — a spreadsheet can hide a column, it
 * cannot invent one.
 */
export function buildMatrixExport(rows = [], columns = [], cells = {}) {
  const exportColumns = [
    { header: 'Recipient', get: (r) => r.__recipient },
    { header: 'Session', get: (r) => r.__session },
    { header: 'Started', get: (r) => r.__started },
    ...columns.map(column => ({ header: column.label || column.name, get: (r) => r[column.name] })),
  ];
  const exportRows = rows.map(row => {
    const out = {
      __recipient: text(row.handle),
      __session: text(row.key),
      __started: text(row.timestamp),
    };
    columns.forEach(column => {
      const cell = matrixCell(cells, row.key, column.name);
      // A cell is either a plain value or an object carrying display text.
      out[column.name] = plainText(cell && typeof cell === 'object' ? (cell.text ?? cell.value ?? cell.display ?? '') : cell);
    });
    return out;
  });
  return { rows: exportRows, columns: exportColumns };
}

export const RESULT_COLUMNS = [
  { header: 'Recipient', get: (r) => r.recipient },
  { header: 'Session', get: (r) => r.sessionId },
  { header: 'Variable', get: (r) => r.variable },
  { header: 'Question', get: (r) => r.question },
  { header: 'Value', get: (r) => r.value },
  { header: 'Confidence', get: (r) => r.confidence },
  { header: 'Has recording', get: (r) => r.hasAudio },
  { header: 'Has upload', get: (r) => r.hasUpload },
];

/**
 * What the Results table shows in its Value cell: an upload reads as its
 * filename, an answer as its text, anything structured as its JSON — never
 * "[object Object]", which is what plain stringification produces for the
 * object a file answer or a multi-select is stored as.
 */
export const resultValue = (row) => {
  const upload = extractGcsPath(row?.value_json);
  if (upload) return uploadFilename(upload);
  if (row?.answer_text != null && row.answer_text !== '') return mapNonAnswer(String(row.answer_text));
  const value = row?.value_json;
  if (typeof value === 'string') return mapNonAnswer(value);
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  // A list or an object keeps its structure as JSON, the way the Value cell
  // renders it — joining it into prose would lose which value was which.
  return json(value);
};

// The kernel writes per-answer confidence as `confidence`; the session-level
// response feed calls the same thing `confidence_weight`. Read either, so the
// column is not silently blank for whichever source a take came from.
const confidencePercent = (row) => {
  const raw = row?.confidence ?? row?.confidence_weight;
  if (raw === null || raw === undefined || raw === '') return '';
  const num = Number(raw);
  return Number.isFinite(num) ? `${Math.round(num * 100)}%` : '';
};

/**
 * The selected sessions' answers, one row per variable.
 *
 * A named recipient's answer row carries no audio path of its own — the pane
 * attaches it from the session's raw responses — so `audioFor` supplies the
 * same lookup the play button uses.
 */
export function buildResultRows(
  resultRows = [],
  {
    recipientFor = () => '',
    questionFor = () => '',
    valueFor,
    audioFor = () => null,
    // A recipient-keyed answer row names its recipient, not its take, so the
    // caller resolves the session the answer belongs to.
    sessionFor = (r) => r?.session_id,
  } = {},
) {
  return resultRows.map(r => ({
    recipient: text(recipientFor(r)),
    sessionId: text(sessionFor(r)),
    variable: text(r.variable_name),
    question: text(questionFor(r)),
    value: plainText(valueFor ? valueFor(r) : resultValue(r)),
    confidence: confidencePercent(r),
    hasAudio: artifactFlag(r.audio_storage_path || audioFor(r)),
    hasUpload: artifactFlag(extractGcsPath(r.value_json) || r.upload_storage_path || r.file_storage_path),
  }));
}

export const TIMELINE_COLUMNS = [
  { header: 'Recipient', get: (r) => r.recipient },
  { header: 'Session', get: (r) => r.sessionId },
  { header: 'Exchange', get: (r) => r.exchange },
  // Three names for one question, in the order a reader wants them: the number
  // the author wrote, the column name results use, then the kernel's record key
  // for support work. The key alone used to be the whole answer, which told the
  // author nothing about which of their questions the row belonged to.
  { header: 'Question', get: (r) => r.question },
  { header: 'Variable', get: (r) => r.variable },
  { header: 'Question ID', get: (r) => r.questionId },
  { header: 'Question text', get: (r) => r.questionText },
  { header: 'Recipient said', get: (r) => r.human },
  { header: 'AI said', get: (r) => r.ai },
  { header: 'AI evaluation', get: (r) => r.evaluation },
  { header: 'Next instruction', get: (r) => r.nextInstruction },
  { header: 'Should advance', get: (r) => r.shouldAdvance },
  { header: 'Extracted data', get: (r) => r.extracted },
  { header: 'Prompts', get: (r) => r.prompts },
  { header: 'Normalized AI payload', get: (r) => r.nap },
  { header: 'Has recording', get: (r) => r.hasAudio },
];

// Several prompts can belong to one exchange. They stay in one cell, separated
// so each is still readable, rather than exploding one exchange into several
// rows and breaking the one-row-per-exchange reading of the file.
const PROMPT_SEPARATOR = '\n\n---\n\n';

// A prompt is an object ({ prompt_type, bytes, prompt_text }), not a string.
// The cell carries the whole prompt, headed by the same `[type - size]` label
// the timeline shows on its button, so the file says what the button opens.
const promptText = (prompt) => {
  if (!prompt) return '';
  if (typeof prompt === 'string') return prompt;
  const size = bytesLabel(Number(prompt.bytes));
  const label = size ? `[${prompt.prompt_type || 'text'} - ${size}]` : `[${prompt.prompt_type || 'text'}]`;
  // Prompts are stored with escaped newlines; restore them so the cell reads
  // as the prompt did, exactly as the prompt viewer does.
  const body = String(prompt.prompt_text || prompt.text || prompt.content || '').replace(/\\n/g, '\n');
  return body ? `${label}\n${body}` : label;
};

// An adapted row keeps every utterance of the turn in `human`; older rows carry
// one flat field. An empty list means the turn had no speech, so fall through
// to the flat fields rather than reporting silence.
const humanSaid = (row) => {
  const said = plainText(row?.human).trim();
  if (said) return said;
  return plainText(row?.raw_transcript || row?.human_text || row?.response_text || '');
};

/** The AI turn's conversational text, all of it, in the order it was said. */
const aiSaid = (row) => {
  const responses = Array.isArray(row?.ai_responses) ? row.ai_responses : [];
  const spoken = responses.map(r => plainText(r?.text)).map(s => s.trim()).filter(Boolean);
  // The same reply arrives twice when a turn logs both an `ai` and a `nap`
  // event; one line per distinct utterance.
  const unique = spoken.filter((s, i) => spoken.indexOf(s) === i);
  if (unique.length) return unique.join('\n');
  return plainText(row?.ai_response || row?.sam_response || row?.ai || row?.conversationalResponse || '');
};

/** The turn's evaluation kind (clarification / advancement), last one wins. */
const latestAiType = (row) => {
  const responses = Array.isArray(row?.ai_responses) ? row.ai_responses : [];
  for (let i = responses.length - 1; i >= 0; i -= 1) {
    if (responses[i]?.type) return responses[i].type;
  }
  return '';
};

/** The last AI response of the turn carrying a structured decision. */
const derivedNap = (row) => {
  const responses = Array.isArray(row?.ai_responses) ? row.ai_responses : [];
  let found = null;
  for (const response of responses) {
    if (response?.nextInstruction || response?.shouldAdvanceSurvey != null || response?.extractedData) found = response;
  }
  return found;
};

/**
 * Every exchange of the selected sessions, in order — not only the rows a
 * search matched. What the engine was asked (Prompts) and what it decided
 * (the Normalized AI Payload, flattened into its own columns beside the raw
 * JSON) are carried in full, beside what the person actually said.
 *
 * The exchange row itself holds no audio path — recordings are keyed by
 * question elsewhere — so `audioFor` supplies the recording lookup.
 */
export function buildTimelineRows(
  selectedSessionIds = [],
  exchangesBySessionId = {},
  recipientFor = () => '',
  {
    promptsFor = () => null,
    napFor = () => null,
    questionTextFor = () => '',
    // { number, variable } for the question this exchange asked. Absent for an
    // exchange whose question is not in the survey structure — a legacy take, or
    // a node that collects no answer — and the record key stands alone then.
    questionIdentityFor = () => null,
    audioFor = () => null,
  } = {},
) {
  const out = [];
  selectedSessionIds.forEach(sessionId => {
    const data = exchangesBySessionId[sessionId];
    const exchanges = data?.exchanges || [];
    [...exchanges]
      .sort((a, b) => Number(a.exchange_seq) - Number(b.exchange_seq))
      .forEach(row => {
        const seq = row.exchange_seq;
        const questionId = row.question_id ? String(row.question_id) : '';
        // The caller may hold prompts and payloads keyed by sequence; the row
        // carries its own, so the export is complete either way.
        const prompts = promptsFor(sessionId, seq) || row.prompts || [];
        const nap = napFor(sessionId, seq) || derivedNap(row);
        const next = (nap && typeof nap === 'object' ? nap.nextInstruction : null) || {};
        const advance = nap && typeof nap === 'object' ? nap.shouldAdvanceSurvey : null;
        const identity = questionId ? questionIdentityFor(questionId, sessionId, seq) : null;
        out.push({
          recipient: text(recipientFor(sessionId)),
          sessionId: text(sessionId),
          exchange: text(seq),
          question: text(identity?.number) || text(identity?.variable) || questionId || (Number(seq) === 0 ? 'START' : ''),
          variable: text(identity?.variable),
          questionId,
          questionText: plainText(questionTextFor(questionId, sessionId, seq)),
          human: humanSaid(row),
          ai: aiSaid(row),
          evaluation: plainText((nap && typeof nap === 'object' && nap.type) || latestAiType(row)),
          nextInstruction: plainText(next.message || next.content || next.text || next.action || ''),
          shouldAdvance: advance === true ? 'Yes' : advance === false ? 'No' : '',
          extracted: json(nap && typeof nap === 'object' ? nap.extractedData : ''),
          prompts: (Array.isArray(prompts) ? prompts : [prompts]).filter(Boolean).map(promptText).join(PROMPT_SEPARATOR),
          nap: json(nap, 2),
          hasAudio: artifactFlag(audioFor(sessionId, row) || row.audio_storage_path || row.audio_url),
        });
      });
  });
  return out;
}
